import React, { useContext, useRef } from 'react';
import { getContext, useCombinedRefs } from 'common';
import Truncate from 'common/Truncate';
import { TabsContext } from './Tabs';
import Styled from './styles';

export interface TabLabelProps extends React.ComponentPropsWithRef<'div'> {
  /** Indicates the current tab is selected or active tab. */
  selected?: boolean;
  /** Indicates the current tab is disabled. */
  disabled?: boolean;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** The label contents. */
  children: string;
  /** @ignore specify variant of tabs */
  variant?: string;
  /** @ignore Callback function to indicate when Tooltip is necessary. */
  enableTooltip?(...args: unknown[]): unknown;
  /** @ignore If true, text takes on pressed color. */
  isPressed?: boolean;
}

/** Displays tab text.  All props other than `children`, `enableTooltip` will be provided to the wrapper element  */
const TabLabel = React.forwardRef<HTMLElement, TabLabelProps>((props: TabLabelProps, forwardedRef) =>
  getContext(({ RTL }) => {
    const { children, enableTooltip, isPressed, ...otherProps } = props;
    const context = useContext(TabsContext);
    const innerRef = useRef(null);
    const ref = useCombinedRefs(forwardedRef, innerRef);
    const isRTL = context.rtl || RTL;
    const tooltipProps = {
      disable: true,
      tooltip: '',
    };

    const handleClampEnd = (clampedObj) => {
      enableTooltip && enableTooltip(clampedObj);
    };
    const truncateStyle = context.variant === 'horizontal-navigation' ? { wordWrap: 'break-word' } : {};

    return (
      <Styled.TabLabel ref={ref} $isPressed={isPressed} {...otherProps} rtl={isRTL} $disableAnimation={context.disableAnimation}>
        <Truncate lines={2} delay={100} text={children} onClampEnd={handleClampEnd} tooltip={tooltipProps} style={truncateStyle} />
      </Styled.TabLabel>
    );
  }));

TabLabel.defaultProps = {
  selected: undefined,
  disabled: undefined,
  theme: {},
  variant: undefined,
  enableTooltip: undefined,
  isPressed: false,
};

TabLabel.displayName = 'TabLabel';

export { TabLabel as default };
